/**
 * Authed fetch wrapper for the same-origin /api/*. Attaches the PKCE-issued
 * access token as `Authorization: Bearer <token>`; a 401 clears the token
 * and bounces the user back through the OIDC authorize flow.
 */

import { API_BASE } from "../config.ts";
import { ApiError } from "./api-error.ts";
import { beginLogin, clearToken, getToken } from "./auth.ts";

export class UnauthorizedError extends Error {
	constructor() {
		super("Unauthorized");
		this.name = "UnauthorizedError";
	}
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
	const token = getToken();
	const headers: Record<string, string> = {
		Accept: "application/json",
		...(init?.headers as Record<string, string> | undefined),
	};
	if (token) headers.Authorization = `Bearer ${token}`;
	const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
	if (res.status === 401) {
		clearToken();
		void beginLogin(window.location.pathname + window.location.search);
		throw new UnauthorizedError();
	}
	return res;
}

async function readJson(res: Response, what: string): Promise<unknown> {
	/*
	 * A response that is not JSON is a fault, never an empty result.
	 *
	 * This used to swallow it: `.json().catch(() => ({}))` turned the SPA's own
	 * HTML — which Static Assets returns for any unrouted /api path — into `{}`,
	 * and a screen reading `data.accounts` off it crashed three frames later with
	 * "not iterable". The cause was invisible at the point of failure.
	 */
	// Every failure carries the status (lib/api-error.ts): the screens decide what to say
	// by it, and a 403 is the only answer that means «not yours to read».
	const type = res.headers.get("content-type") ?? "";
	if (!type.includes("json")) {
		throw new ApiError(
			`${what}: expected JSON, got ${type || "no content-type"} (${res.status})`,
			res.status,
		);
	}
	const parsed = await res.json().catch(() => null);
	if (parsed === null) throw new ApiError(`${what}: malformed JSON (${res.status})`, res.status);
	return parsed;
}

async function body<T>(res: Response, what: string): Promise<T> {
	const parsed = await readJson(res, what);
	if (!res.ok)
		throw new ApiError(
			(parsed as { error?: string }).error ?? `${what} (${res.status})`,
			res.status,
		);
	return parsed as T;
}

/**
 * The status and the JSON, with a failure left as an answer rather than thrown.
 *
 * For the two campaign writes only: their 502s say more than `error` — that the
 * campaign exists (`created: true`), that nobody can tell (`created: "unknown"`),
 * or what TikTok read back — and a thrown message would keep only the words.
 * lib/campaigns.ts decides what each answer means. A body that is not JSON is
 * still a fault and still throws.
 */
async function answer(method: string, path: string, payload?: unknown): Promise<Answer> {
	const res = await authFetch(path, {
		method,
		// A payload-less read goes without a body and without the header that announces
		// one: `campaign` below is a GET, and its 404 is information rather than a fault.
		...(payload === undefined
			? {}
			: { headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
	});
	return {
		status: res.status,
		body: await readJson(res, `${method} ${path} failed`),
	};
}

const getJson = async <T>(path: string) => body<T>(await authFetch(path), `GET ${path} failed`);

const send = async <T>(method: string, path: string, payload?: unknown) =>
	body<T>(
		await authFetch(path, {
			method,
			headers: { "Content-Type": "application/json" },
			body: payload === undefined ? undefined : JSON.stringify(payload),
		}),
		`${method} ${path} failed`,
	);

// ── Shapes ───────────────────────────────────────────────────────────

export interface Me {
	id: string;
	email?: string;
	role?: string;
	appAccess?: string[];
}

// The ladder lives in ./roles.ts so a test runner can load it — api.ts imports
// config.ts, which reads import.meta.env, and node --test has no such thing.
// Imported for the shapes below and re-exported so callers keep one import site.
import type { Role } from "./roles.ts";
export type { Role };
export { roleAtLeast } from "./roles.ts";
export type EntityType = "agents" | "tiktok_accounts" | "proxies";
export type Permission = "read" | "write";

export interface Grant {
	entityType: EntityType;
	entityId: string;
	permission: Permission;
}

export interface MyAccess {
	userId: string;
	email: string | null;
	role: Role;
	/** True when the role comes from the platform, not a seated member row. */
	inherited: boolean;
	status: string;
	grants: Grant[];
	canAssign: Role[];
}

export interface Member {
	id: string;
	userId: string;
	email: string | null;
	displayName: string | null;
	role: Role;
	status: "active" | "suspended";
	updatedAt: string;
	grants: Grant[];
}

export interface Agent {
	id: string;
	name: string;
	kind: string;
	description: string | null;
	status: string;
}

export interface ProxyRow {
	id: string;
	label: string;
	protocol: string;
	host: string;
	port: number;
	country: string | null;
	credRef: string | null;
	status: string;
}

export interface TikTokAccount {
	id: string;
	/** TikTok's advertiser_id — a numeric id sent as a string. */
	advertiserId: string;
	name: string;
	currency: string | null;
	/** TikTok's advertiser status, raw (e.g. `STATUS_ENABLE`); null until synced. */
	advertiserStatus: string | null;
	/** advertiser/info `timezone`: a GMT offset or a Region/City name; null until synced. */
	timezone: string | null;
	/*
	 * `spendCap` is in the contract and always null in v1: TikTok's advertiser/info
	 * has no spending cap. It is deliberately not declared here, so nothing on this
	 * side can read it. It comes back with the Business Center budget decision.
	 */
	status: string;
	syncedAt: string | null;
}

export type OrgRole = "owner" | "manager" | "member";

export interface Team {
	id: string;
	name: string;
	slug: string;
	status: "pending" | "active" | "suspended" | "rejected";
	requestedBy: string;
	note: string | null;
	memberCount?: number;
	grants?: Grant[];
}

export interface TeamMember {
	id: string;
	userId: string;
	email: string | null;
	role: OrgRole;
	status: string;
}

/** What the console renders for this caller — see lib/principal.ts. */
export interface Standing {
	userId: string;
	email: string | null;
	standing: "platform" | "tenant" | "pending" | "unaffiliated";
	platform: { role: Role; inherited: boolean } | null;
	team: {
		id: string;
		name: string;
		status: Team["status"];
		role: OrgRole;
		grants: Grant[];
	} | null;
}

/** The objectives `POST /api/v1/campaigns` accepts (docs/API-CONTRACT.md). */
export type CampaignObjective =
	| "TRAFFIC"
	| "PRODUCT_SALES"
	| "WEB_CONVERSIONS"
	| "LEAD_GENERATION"
	| "REACH"
	| "VIDEO_VIEWS"
	| "ENGAGEMENT";

export type CampaignBudgetMode =
	| "BUDGET_MODE_DAY"
	| "BUDGET_MODE_TOTAL"
	| "BUDGET_MODE_DYNAMIC_DAILY_BUDGET"
	| "BUDGET_MODE_INFINITE";

export type SpecialIndustry = "HOUSING" | "EMPLOYMENT" | "CREDIT";

/** The switch. `DELETE` is not a status change in v1 and is refused. */
export type CampaignSwitch = "ENABLE" | "DISABLE";

/** One campaign, as `GET /api/v1/campaigns?advertiser=` returns it. */
/**
 * One ad group, as `GET /api/v1/adgroups` and `.../adgroups/:id` report it.
 *
 * `operationStatus` carries one value a campaign's cannot: `FROZEN`, which TikTok's
 * `adgroup/get` documents as "terminated and cannot be enabled" and `campaign/get`
 * does not document at all. The screen therefore has three states to render here and
 * two one level up — and the type is a `string` rather than a union for the reason the
 * campaign's is: what TikTok may report is not this console's to close.
 */
/**
 * The only status this console sends to an ad group. `ENABLE` is refused by the worker
 * by name, and `DELETE` is not expressible — the platform function takes no status at
 * all, so a union of one is the honest type rather than a narrowing of TikTok's enum.
 */
export type AdGroupSwitch = "DISABLE";

export interface AdGroup {
	id: string;
	campaignId: string;
	name: string;
	operationStatus: string;
	secondaryStatus: string | null;
	budgetMode: string;
	/** A float in the account's currency, exactly as TikTok reports it — never converted. */
	budget: number;
	scheduleType: string;
	scheduleStartTime: string;
	optimizationGoal: string;
	billingEvent: string;
	pacing: string;
	createTime: string;
	modifyTime: string;
}

export interface Campaign {
	id: string;
	name: string;
	objectiveType: string;
	/** The switch, `ENABLE` or `DISABLE`. */
	operationStatus: string;
	/** What TikTok is actually doing with the campaign; null when TikTok does not say. */
	secondaryStatus: string | null;
	budgetMode: string;
	/** A float in the account's currency, exactly as TikTok reports it — never converted. */
	budget: number;
	/** As TikTok writes it, e.g. `2026-09-14 10:00:00`. */
	createTime: string;
	modifyTime: string;
}

/**
 * What TikTok reported about the read: its `page_info`, renamed. `totalNumber` is every
 * campaign under the advertiser, not the rows in this answer.
 */
export interface CampaignPage {
	page: number;
	pageSize: number;
	totalPages: number;
	totalNumber: number;
}

/** The v1 create body. Nothing else is accepted, and eight fields are refused on presence. */
export interface CampaignCreate {
	advertiser: string;
	name: string;
	objectiveType: CampaignObjective;
	budgetMode: CampaignBudgetMode;
	budget?: number;
	specialIndustries?: SpecialIndustry[];
}

/** A write's answer read whole, for the routes whose failures carry more than `error`. */
export interface Answer {
	status: number;
	body: unknown;
}

/** One dependency check. `target` names the exact database/dataset reached. */
export interface Check {
	state: "ok" | "error" | "skipped";
	target: string;
	detail?: string;
}

export interface Smoke {
	db?: Check;
	bq?: Check;
	tiktok?: Check;
}

export interface ColumnInfo {
	name: string;
	type: string;
	nullable: boolean;
	description: string | null;
}

export interface IndexInfo {
	name: string;
	definition: string;
	unique: boolean;
	primary: boolean;
}

export interface ConstraintInfo {
	name: string;
	definition: string;
	kind: "check" | "foreign key";
}

export interface TableInfo {
	name: string;
	description: string | null;
	rows: number | null;
	columns: ColumnInfo[];
	/** Always present, `[]` for BigQuery. Worker script and Static Assets go up
	 *  in one `wrangler deploy`, so this SPA never meets a worker that does not
	 *  send it. */
	indexes: IndexInfo[];
	constraints: ConstraintInfo[];
}

export interface StoreSchema {
	label: string;
	tables: TableInfo[];
	error?: string;
}

export interface Schema {
	cloudsql: StoreSchema;
	bigquery: StoreSchema;
}

/**
 * One line of the append-only trail.
 *
 * Snake_case on purpose, and against this project's own convention: the audit
 * endpoint returns raw Postgres rows rather than mapping them the way every
 * other store function does, so `actor_id` and `created_at` are what actually
 * arrives on the wire. Typing it honestly here beats a camelCase interface that
 * would be quietly wrong at runtime. If the endpoint is ever fixed to match
 * CLAUDE.md, this interface is where it shows.
 */
export interface AuditEntry {
	actor_id: string;
	action: string;
	subject_id: string | null;
	detail: Record<string, unknown> | null;
	created_at: string;
}

// ── Calls ────────────────────────────────────────────────────────────

export const api = {
	me: () => getJson<Me>("/api/me"),
	smoke: () => getJson<Smoke>("/api/v1/smoke"),

	standing: () => getJson<Standing>("/api/v1/orgs/me"),
	registerTeam: (payload: { name: string; note?: string }) =>
		send<{ team: Team }>("POST", "/api/v1/orgs", payload),
	teams: () =>
		getJson<{
			teams: Team[];
			accountHolders: Record<string, { orgId: string; orgName: string }>;
		}>("/api/v1/orgs"),
	setTeamStatus: (orgId: string, status: Team["status"]) =>
		send<{ team: Team }>("PUT", `/api/v1/orgs/${orgId}/status`, { status }),
	setTeamGrants: (
		orgId: string,
		payload: {
			entity_type: EntityType;
			permission: Permission;
			entity_ids: string[];
		},
	) => send<{ ok: true }>("PUT", `/api/v1/orgs/${orgId}/grants`, payload),
	teamMembers: (orgId: string) =>
		getJson<{ members: TeamMember[] }>(`/api/v1/orgs/${orgId}/members`),
	setTeamMember: (orgId: string, userId: string, payload: { role: OrgRole; email?: string }) =>
		send<{ member: TeamMember }>("PUT", `/api/v1/orgs/${orgId}/members/${userId}`, payload),
	removeTeamMember: (orgId: string, userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/orgs/${orgId}/members/${userId}`),

	myAccess: () => getJson<MyAccess>("/api/v1/access/me"),
	members: () => getJson<{ members: Member[] }>("/api/v1/access/members"),
	setMember: (userId: string, payload: { role: Role; status?: string; email?: string }) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}`,
			payload,
		),
	removeMember: (userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/access/members/${encodeURIComponent(userId)}`),
	/** Replaces the whole set for one entity type + permission. */
	setGrants: (
		userId: string,
		payload: {
			entity_type: EntityType;
			permission: Permission;
			entity_ids: string[];
		},
	) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}/grants`,
			payload,
		),

	/*
	 * `truncated` is required on all three, and on `/api/v1/accounts` below.
	 *
	 * Not optional-because-the-server-might-not-send-it: every one of those
	 * endpoints returns it unconditionally, including the platform-admin branch
	 * that reads no mirror and reports `false` anyway. And the worker script and
	 * the Static Assets go up in a single `wrangler deploy`, so this SPA never
	 * meets a worker that predates the field.
	 *
	 * Written here rather than only in the commit that removed the `?`, because
	 * that is the mistake this line exists to stop: the same `?` was taken out of
	 * `TableInfo.indexes` for this same reason and reappeared here two diffs
	 * later. A guard held up by its explanation comes back whenever the
	 * explanation sounds prudent again — so the refutation has to live where the
	 * guard lived, not in the history.
	 */
	agents: () => getJson<{ agents: Agent[]; truncated: boolean }>("/api/v1/agents"),
	createAgent: (payload: {
		name: string;
		kind?: string;
		description?: string;
	}) => send<{ agent: Agent }>("POST", "/api/v1/agents", payload),
	updateAgent: (id: string, payload: { name?: string; kind?: string; status?: string }) =>
		send<{ agent: Agent }>("PATCH", `/api/v1/agents/${id}`, payload),
	deleteAgent: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/agents/${id}`),

	proxies: () => getJson<{ proxies: ProxyRow[]; truncated: boolean }>("/api/v1/proxies"),
	createProxy: (payload: {
		label: string;
		protocol?: string;
		host: string;
		port: number;
		country?: string;
		cred_ref?: string;
	}) => send<{ proxy: ProxyRow }>("POST", "/api/v1/proxies", payload),
	updateProxy: (
		id: string,
		payload: {
			label?: string;
			host?: string;
			port?: number;
			country?: string;
			status?: string;
		},
	) => send<{ proxy: ProxyRow }>("PATCH", `/api/v1/proxies/${id}`, payload),
	deleteProxy: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/proxies/${id}`),

	tiktokAccounts: () =>
		getJson<{ accounts: TikTokAccount[]; truncated: boolean }>("/api/v1/tiktok-accounts"),
	syncTikTokAccounts: () => send<{ synced: number }>("POST", "/api/v1/tiktok-accounts/sync"),

	/**
	 * One page of campaigns. `page` defaults to 1 and `pageSize` to the worker's own default
	 * (1000, TikTok's documented maximum); `truncated` says TikTok counts pages after this one,
	 * and never that a filter narrowed the answer.
	 */
	campaigns: (advertiser: string, paging: { page?: number; pageSize?: number } = {}) => {
		const query = new URLSearchParams({ advertiser });
		if (paging.page !== undefined) query.set("page", String(paging.page));
		if (paging.pageSize !== undefined) query.set("pageSize", String(paging.pageSize));
		return getJson<{ campaigns: Campaign[]; page: CampaignPage; truncated: boolean }>(
			`/api/v1/campaigns?${query.toString()}`,
		);
	},
	/**
	 * `campaign` is not optional, and the worker refuses the call without it: a list
	 * across an advertiser is a different property, which no route offers.
	 */
	adGroups: (
		advertiser: string,
		campaign: string,
		paging: { page?: number; pageSize?: number } = {},
	) => {
		const query = new URLSearchParams({ advertiser, campaign });
		if (paging.page !== undefined) query.set("page", String(paging.page));
		if (paging.pageSize !== undefined) query.set("pageSize", String(paging.pageSize));
		return getJson<{ adGroups: AdGroup[]; page: CampaignPage; truncated: boolean }>(
			`/api/v1/adgroups?${query.toString()}`,
		);
	},
	/**
	 * A stop, and nothing else. The body carries `status` because the contract does; the
	 * type makes `ENABLE` unwritable here rather than leaving it to the worker's 400.
	 *
	 * Read whole through `answer`, like the campaign switch and for the same reason: this
	 * route's 502 says more than `error` — it carries what the read-back saw, and `FROZEN`
	 * there is a different answer from any other status. `lib/ad-groups.ts` decides which.
	 * A thrown message would keep only the words.
	 */
	setAdGroupStatus: (id: string, payload: { advertiser: string; status: AdGroupSwitch }) =>
		answer("POST", `/api/v1/adgroups/${encodeURIComponent(id)}/status`, payload),
	adGroup: (id: string, advertiser: string) =>
		getJson<AdGroup>(
			`/api/v1/adgroups/${encodeURIComponent(id)}?advertiser=${encodeURIComponent(advertiser)}`,
		),
	/** Read whole: a 502 can mean the campaign exists. lib/campaigns.ts `createOutcome`. */
	createCampaign: (payload: CampaignCreate) => answer("POST", "/api/v1/campaigns", payload),
	/**
	 * One campaign, by id. **Answered, not thrown**, because a 404 here is a fact and
	 * not a fault: after a create it is the difference between "TikTok does not have
	 * it" and "the read failed", and the lock on Create turns on exactly that.
	 *
	 * Read by id rather than by list on purpose — see `foundById` in lib/campaigns.ts
	 * and the list's lag in docs/API-CONTRACT.md.
	 */
	campaign: (id: string, advertiser: string) =>
		answer(
			"GET",
			`/api/v1/campaigns/${encodeURIComponent(id)}?advertiser=${encodeURIComponent(advertiser)}`,
		),
	/** Read whole: a 502 can carry what TikTok read back. lib/campaigns.ts `statusOutcome`. */
	setCampaignStatus: (id: string, payload: { advertiser: string; status: CampaignSwitch }) =>
		answer("POST", `/api/v1/campaigns/${encodeURIComponent(id)}/status`, payload),

	schema: () => getJson<Schema>("/api/v1/system/schema"),
	audit: () => getJson<{ entries: AuditEntry[] }>("/api/v1/access/audit"),
};
